/**
 * Test Page for Quiz Database Upload (Phần 4)
 * Upload folder B06 với TN/DS/TL quiz JSON và extract images
 */

import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { teacherContentService } from '../../services/teacher/TeacherContentService'
import toast from 'react-hot-toast'
import { Upload, CheckCircle, XCircle, Loader, FolderOpen } from 'lucide-react'

const TestQuizUpload: React.FC = () => {
    const { user } = useAuth()
    const [uploading, setUploading] = useState(false)
    const [uploadResults, setUploadResults] = useState<string[]>([])

    // Thông tin bài học
    const [lop, setLop] = useState('11')
    const [chuong, setChuong] = useState('1')
    const [bai, setBai] = useState('1')

    /**
     * Handle folder upload (B06)
     * Folder structure: B06/TN/questions_tn.json, B06/DS/questions_ds.json, B06/TL/questions_tl.json
     */
    const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files
        if (!fileList || fileList.length === 0) {
            toast.error('Vui lòng chọn thư mục Bxx (ví dụ: B06)')
            return
        }

        // ✅ Check user login
        if (!user || !user.id) {
            toast.error('❌ Vui lòng đăng nhập trước khi upload!')
            console.error('❌ [QUIZ TEST] User not logged in')
            return
        }

        const files = Array.from(fileList)
        console.log(`📁 [QUIZ TEST] Selected ${files.length} files`)

        // Group files by type (TN/DS/TL)
        const groups: Record<'TN' | 'DS' | 'TL', File | null> = { TN: null, DS: null, TL: null }

        files.forEach((file) => {
            const relPath = (file as any).webkitRelativePath || file.name
            console.log(`📄 [QUIZ TEST] File: ${relPath}`)

            const segments = relPath.split(/[/\\]/).filter(Boolean)

            // Expected structure: Bxx/TN/questions_tn.json or Bxx/TN/*.json
            if (segments.length < 3) return

            const typeFolder = segments[segments.length - 2].toUpperCase()
            const fileName = segments[segments.length - 1].toLowerCase()

            console.log(`🔍 [QUIZ TEST] Type folder: ${typeFolder}, File: ${fileName}`)

            if (!fileName.endsWith('.json')) return

            if (typeFolder === 'TN' || typeFolder === 'DS' || typeFolder === 'TL') {
                if (!groups[typeFolder]) {
                    groups[typeFolder] = file
                    console.log(`✅ [QUIZ TEST] Assigned ${fileName} to ${typeFolder}`)
                }
            }
        })

        console.log(`📊 [QUIZ TEST] Groups:`, {
            TN: groups.TN?.name || 'None',
            DS: groups.DS?.name || 'None',
            TL: groups.TL?.name || 'None'
        })

        if (!groups.TN && !groups.DS && !groups.TL) {
            toast.error('Không tìm thấy file JSON trong TN/DS/TL. Cấu trúc: Bxx/TN/*.json')
            return
        }

        // Upload - ✅ PARALLEL UPLOAD for faster speed
        setUploading(true)
        setUploadResults([])
        const results: string[] = []
        const errors: string[] = []

        try {
            // ✅ AUTO-DETECT lớp/chương/bài từ first question trong quiz JSON
            let detectedLop = lop
            let detectedChuong = chuong
            let detectedBai = bai

            // Try to read first file to auto-detect
            for (const type of ['TN', 'DS', 'TL'] as const) {
                const file = groups[type]
                if (file) {
                    try {
                        const text = await file.text()
                        const quizData = JSON.parse(text)

                        // Get questions array (support multiple formats)
                        const questions = quizData.questions ||
                            quizData.questions_tn ||
                            quizData.questions_ds ||
                            quizData.questions_tl || []

                        if (questions.length > 0) {
                            const firstQ = questions[0]

                            // Parse: "L11" → "11", "C2" → "2", "B06" → "06"
                            if (firstQ.grade) {
                                detectedLop = firstQ.grade.toString().replace(/^L/i, '')
                                console.log(`📍 [AUTO-DETECT] Lớp: ${detectedLop} (from ${firstQ.grade})`)
                            }
                            if (firstQ.chapter) {
                                detectedChuong = firstQ.chapter.toString().replace(/^C/i, '')
                                console.log(`📍 [AUTO-DETECT] Chương: ${detectedChuong} (from ${firstQ.chapter})`)
                            }
                            if (firstQ.lesson) {
                                detectedBai = firstQ.lesson.toString().replace(/^B/i, '')
                                console.log(`📍 [AUTO-DETECT] Bài: ${detectedBai} (from ${firstQ.lesson})`)
                            }

                            toast.success(`✅ Tự động nhận diện: Lớp ${detectedLop}, Chương ${detectedChuong}, Bài ${detectedBai}`)
                            break
                        }
                    } catch (e) {
                        console.warn(`⚠️ Could not auto-detect from ${type}:`, e)
                    }
                }
            }

            // ✅ Upload TN, DS, TL in parallel (cùng lúc) with auto-detected values
            const uploadPromises = (['TN', 'DS', 'TL'] as const).map(async (type) => {
                const file = groups[type]
                if (!file) return null

                try {
                    console.log(`📤 [QUIZ TEST] Uploading ${type}...`)
                    console.log(`   File: ${file.name}, Size: ${file.size} bytes`)

                    // Read and preview JSON
                    const text = await file.text()
                    const quizData = JSON.parse(text)
                    const questionCount = quizData.questions?.length ||
                        quizData.questions_tn?.length ||
                        quizData.questions_ds?.length ||
                        quizData.questions_tl?.length || 0
                    console.log(`   Questions: ${questionCount}`)

                    // ✅ Upload with AUTO-DETECTED lop/chuong/bai
                    await teacherContentService.uploadQuizJson(
                        user.id,
                        detectedLop,
                        detectedChuong,
                        detectedBai,
                        type,
                        file
                    )

                    console.log(`✅ [QUIZ TEST] Success ${type}`)
                    return { success: true, type, count: questionCount }
                } catch (typeError: any) {
                    console.error(`❌ [QUIZ TEST] Failed ${type}:`, typeError)
                    return { success: false, type, error: typeError.message }
                }
            })

            // Wait for all uploads to complete
            const uploadResults = await Promise.all(uploadPromises)

            // Process results
            uploadResults.forEach((result) => {
                if (!result) return

                if (result.success) {
                    results.push(`✅ ${result.type}: ${result.count} câu hỏi`)
                } else {
                    errors.push(`❌ ${result.type}: ${result.error}`)
                }
            })

            setUploadResults([...results, ...errors])

            if (errors.length === 0 && results.length > 0) {
                toast.success(`Upload thành công ${results.length} loại quiz! (L${detectedLop} C${detectedChuong} B${detectedBai})`)
            } else if (errors.length > 0) {
                toast.error(`Upload thất bại: ${errors.length} lỗi`)
            }
        } catch (error: any) {
            console.error('❌ [QUIZ TEST] Error:', error)
            toast.error(error.message)
        } finally {
            setUploading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-200">
                        <FolderOpen className="w-8 h-8 text-indigo-600" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">
                                Test Upload Quiz Database
                            </h1>
                            <p className="text-gray-600 mt-1">Phần 4: Upload folder Bxx chứa TN/DS/TL</p>
                        </div>
                    </div>

                    {/* Lesson Info */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Lớp
                            </label>
                            <input
                                type="text"
                                value={lop}
                                onChange={(e) => setLop(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="11"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Chương
                            </label>
                            <input
                                type="text"
                                value={chuong}
                                onChange={(e) => setChuong(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="1"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Bài
                            </label>
                            <input
                                type="text"
                                value={bai}
                                onChange={(e) => setBai(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="1"
                            />
                        </div>
                    </div>

                    {/* Upload Folder */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <Upload className="w-5 h-5" />
                            Upload Folder Quiz (B06)
                        </h2>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">📁 Cấu trúc folder yêu cầu:</p>
                            <div className="bg-white p-3 rounded-lg font-mono text-xs text-gray-700">
                                <div>B06/</div>
                                <div className="ml-4">├── TN/</div>
                                <div className="ml-8">│   └── questions_tn.json</div>
                                <div className="ml-4">├── DS/</div>
                                <div className="ml-8">│   └── questions_ds.json</div>
                                <div className="ml-4">└── TL/</div>
                                <div className="ml-8">    └── questions_tl.json</div>
                            </div>
                        </div>

                        <label className="relative block">
                            <input
                                type="file"
                                // @ts-ignore
                                webkitdirectory=""
                                directory=""
                                multiple
                                onChange={handleFolderUpload}
                                disabled={uploading}
                                className="hidden"
                            />
                            <div className="flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer">
                                {uploading ? (
                                    <>
                                        <Loader className="w-5 h-5 animate-spin" />
                                        <span>Đang upload...</span>
                                    </>
                                ) : (
                                    <>
                                        <FolderOpen className="w-5 h-5" />
                                        <span className="font-semibold">Chọn Folder Bxx (ví dụ: B06)</span>
                                    </>
                                )}
                            </div>
                        </label>
                    </div>

                    {/* Upload Results */}
                    {uploadResults.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Kết quả Upload:</h3>
                            <div className="space-y-2">
                                {uploadResults.map((result, index) => (
                                    <div
                                        key={index}
                                        className={`flex items-start gap-3 p-3 rounded-lg ${result.startsWith('✅')
                                            ? 'bg-green-50 text-green-800'
                                            : 'bg-red-50 text-red-800'
                                            }`}
                                    >
                                        {result.startsWith('✅') ? (
                                            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                        ) : (
                                            <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                        )}
                                        <span className="text-sm font-medium">{result}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Instructions */}
                    <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-900 mb-2">📝 Hướng dẫn:</h4>
                        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                            <li>Nhập thông tin Lớp, Chương, Bài</li>
                            <li>Click "Chọn Folder Bxx" và chọn folder B06</li>
                            <li>Hệ thống sẽ tự động:
                                <ul className="ml-6 mt-1 space-y-1 list-disc list-inside">
                                    <li>Tìm file questions_tn.json, questions_ds.json, questions_tl.json</li>
                                    <li>Extract images từ quiz JSON</li>
                                    <li>Download và lưu vào: /teacher_content/.../L11/C1/B01/images/</li>
                                    <li>Upload quiz JSON lên server</li>
                                </ul>
                            </li>
                            <li>Mở Console (F12) để xem log chi tiết</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TestQuizUpload
